"use client";

import { getInstance } from "@asosteam/asos-web-features-browser";
import {
  useEffect,
  useState,
  createContext,
  useContext,
  ReactNode,
  useMemo,
} from "react";
import { logClientError } from "@repo/next-utils/newrelic-client-utils";

type FeaturesInstance = ReturnType<typeof getInstance>;
type FeatureVariables = {
  [key: string]: unknown;
};

type FeatureFlag = {
  isEnabled(): boolean;
  getVariables(): FeatureVariables;
};

type FeaturesInstanceWithFeature = FeaturesInstance & {
  getFeature(featureKey: string): FeatureFlag;
};

type FeaturesContextType = {
  instance: FeaturesInstance;
};

export type FeaturesProviderProps = {
  children: ReactNode;
  sdkKey?: string;
  verbose?: boolean;
  coreAttributes?: Record<string, unknown>;
};

export type FeatureProviderProps = {
  children: ReactNode;
  featureKey: string;
};

const defaultContext: FeaturesContextType = {} as FeaturesContextType;
const FeaturesContext = createContext<FeaturesContextType>(defaultContext);
const FeatureContext = createContext<string>("");

export const FeaturesInstanceProvider = ({
  sdkKey,
  children,
  verbose = false,
  coreAttributes,
}: FeaturesProviderProps) => {
  const asosFeaturesInstance = useMemo(() => {
    if (typeof window === "undefined") {
      return null;
    }

    return getInstance(
      {
        sdkKey,
        datafileOptions: {
          urlTemplate: "https://cdn.optimizely.com/datafiles/%s.json",
        },
        logger: {
          log: (level: number, message: string) => {
            if (level >= 4 && verbose) {
              console.warn("Optimizely Log:", level, message);
            }
          },
        },
        errorHandler: {
          handleError: (
            error: Error,
            attributes: Record<string, string | number | boolean>,
          ) => {
            logClientError({ error, attributes });
            if (verbose) {
              console.error(error.message);
            }
          },
        },
      },
      coreAttributes,
    );
  }, [sdkKey, verbose, coreAttributes]);

  if (typeof window === "undefined") {
    return <>{children}</>;
  }

  return (
    <FeaturesContext.Provider value={{ instance: asosFeaturesInstance! }}>
      {children}
    </FeaturesContext.Provider>
  );
};

export const Root = ({ children, featureKey }: FeatureProviderProps) => {
  return (
    <FeatureContext.Provider value={featureKey}>
      {children}
    </FeatureContext.Provider>
  );
};

export const useFeature = (featureKey: string): FeatureFlag | undefined => {
  const [instance, setInstance] = useState<FeaturesInstanceWithFeature | null>(
    null,
  );
  const context = useContext<FeaturesContextType>(FeaturesContext);

  if (context === defaultContext && typeof window !== "undefined") {
    throw new Error(
      "useFeature must be used within a FeaturesInstanceProvider",
    );
  }

  useEffect(() => {
    let cancelled = false;
    if (context.instance) {
      context.instance.onReady().then(() => {
        if (!cancelled) {
          setInstance(context.instance as FeaturesInstanceWithFeature);
        }
      });
    }
    return () => {
      cancelled = true;
    };
  }, [context.instance]);

  return instance?.getFeature(featureKey);
};

export const Flag = ({
  whenInactive,
  children,
}: {
  whenInactive?: boolean;
  children: ReactNode;
}) => {
  const featureKey = useContext(FeatureContext);
  const flag = useFeature(featureKey);

  if (!flag) return null;

  const isShown = whenInactive ? !flag.isEnabled() : flag.isEnabled();

  return isShown ? <>{children}</> : null;
};

export const Variant = ({
  variable,
  value,
  children,
}: {
  variable?: string;
  value?: number | string | boolean | Record<string, unknown>;
  children: ReactNode;
}) => {
  const featureKey = useContext(FeatureContext);
  const flag = useFeature(featureKey);

  if (!flag) return null;

  const isEnabled = flag.isEnabled();
  const isBucketed = !!(
    value !== undefined &&
    variable !== undefined &&
    flag.getVariables() &&
    flag.getVariables()[variable] === value
  );

  const isShown = isEnabled && isBucketed;

  return isShown ? <>{children}</> : null;
};

export const Waiting = ({ children }: { children: ReactNode }) => {
  const featureKey = useContext(FeatureContext);
  const flag = useFeature(featureKey);

  return flag ? null : children;
};
